import { DoorOpen, Monitor, Plus, Undo2 } from 'lucide-react';
import type { Presence, PresenceEquipment } from 'shared';
import { formatClock } from '@/lib/presenceView';
import { formatRemaining, isOverdue } from '@/lib/sessionTime';

interface PresenceCardProps {
  presence: Presence;
  /** True while any action on this participant is in flight. */
  busy: boolean;
  onHandOver: () => void;
  onTakeBack: (equipment: PresenceEquipment) => void;
  onCheckOut: () => void;
}

/** One participant who is in the centre, and what staff can do for them. */
export default function PresenceCard({
  presence,
  busy,
  onHandOver,
  onTakeBack,
  onCheckOut,
}: PresenceCardProps) {
  const holds = presence.equipment.length > 0;

  return (
    <li className="card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-base font-semibold text-foreground truncate">
            {presence.participantName}
          </p>
          <p className="text-xs text-muted-foreground tabular-nums">
            ID {presence.participantId} · desde as {formatClock(presence.checkedInAt)}
            {presence.checkedInByName
              ? ` · entrada registada por ${presence.checkedInByName}`
              : ' · entrada pelo telemóvel'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onHandOver}
            disabled={busy}
            aria-label={`Entregar equipamento a ${presence.participantName}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Entregar equipamento
          </button>
          <button
            type="button"
            onClick={onCheckOut}
            disabled={busy || holds}
            title={holds ? 'Regista primeiro a devolução do equipamento' : undefined}
            aria-label={`Terminar visita de ${presence.participantName}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-destructive/30 bg-destructive/5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <DoorOpen className="w-4 h-4" />
            Terminar visita
          </button>
        </div>
      </div>

      {holds ? (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {presence.equipment.map((e) => (
            <li key={e.assignmentId} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0 flex items-center gap-2">
                <Monitor className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{e.equipmentName}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    desde as {formatClock(e.startedAt)} ·{' '}
                    <span className={isOverdue(e.expiresAt ? new Date(e.expiresAt) : null) ? 'text-destructive font-medium' : ''}>
                      {formatRemaining(e.expiresAt ? new Date(e.expiresAt) : null)}
                    </span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onTakeBack(e)}
                disabled={busy}
                aria-label={`Devolver ${e.equipmentName} de ${presence.participantName}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors shrink-0"
              >
                <Undo2 className="w-4 h-4" />
                Devolver
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Sem equipamento.</p>
      )}
    </li>
  );
}
